<template>
  <div class="compliance-sign">
    <div class="compliance-sign__card" v-if="!submitted">
      <div v-if="loading" class="text-center py-5">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p class="mt-2">Loading form...</p>
      </div>

      <div v-if="errorMessage" class="text-center py-5">
        <i class="fa fa-exclamation-triangle fa-2x" style="color: #f56c6c"></i>
        <p class="mt-2" style="color: #f56c6c">{{ errorMessage }}</p>
      </div>

      <div v-if="formData && !errorMessage && !loading">
        <div class="compliance-sign__logo-wrapper">
          <img src="/static/Oppo_LogoFinal-02.png" alt="OPPO" class="compliance-sign__logo" />
        </div>
        <h2 class="compliance-sign__title">{{ formData.formDisplayName }}</h2>
        <p class="compliance-sign__client">For: <strong>{{ formData.clientName }}</strong></p>

        <!-- Form body text (shown for all forms that have it) -->
        <div v-if="formData.formBody && !hasFormFields" class="compliance-sign__body" v-html="formData.formBody"></div>

        <!-- Forms WITH interactive fields: integrated inline rendering -->
        <div v-if="hasFormFields" class="compliance-sign__integrated">
          <template v-for="(section, sIdx) in groupedFields">
            <!-- Visible section -->
            <div v-if="isSectionVisible(section)" :key="'sec-' + sIdx" class="compliance-sign__section">
              <h4 v-if="section.name" class="compliance-sign__section-header">{{ section.name }}</h4>
              <div v-if="section.description" class="compliance-sign__section-desc" v-html="formatDescription(section.description)"></div>

              <template v-for="field in section.fields">
                <!-- Info-only field (no input, just the section description handles it) -->
                <template v-if="field.type === 'info'"></template>

                <!-- YES/NO field -->
                <div v-else-if="field.type === 'yes_no'" :key="field.id" class="compliance-sign__field">
                  <label class="compliance-sign__field-label">
                    {{ field.label }}
                    <span v-if="field.required" class="compliance-sign__required">*</span>
                  </label>
                  <div class="compliance-sign__yes-no-buttons">
                    <el-button
                      :type="formResponses[field.id] === 'yes' ? 'primary' : 'default'"
                      size="medium"
                      @click="setResponse(field.id, 'yes')"
                    >Yes</el-button>
                    <el-button
                      :type="formResponses[field.id] === 'no' ? 'danger' : 'default'"
                      size="medium"
                      @click="setResponse(field.id, 'no')"
                    >No</el-button>
                  </div>
                </div>

                <!-- Initials field -->
                <div v-else-if="field.type === 'initials'" :key="field.id" class="compliance-sign__field compliance-sign__field--initials">
                  <div class="compliance-sign__initials-row">
                    <el-input
                      :value="formResponses[field.id]"
                      @input="setResponse(field.id, $event)"
                      placeholder="Initials"
                      maxlength="5"
                      class="compliance-sign__initials-input"
                    />
                    <label class="compliance-sign__field-label compliance-sign__field-label--inline">
                      {{ field.label }}
                      <span v-if="field.required" class="compliance-sign__required">*</span>
                    </label>
                  </div>
                </div>

                <!-- Checkbox group field -->
                <div v-else-if="field.type === 'checkbox_group'" :key="field.id" class="compliance-sign__field">
                  <label class="compliance-sign__field-label">
                    {{ field.label }}
                    <span v-if="field.required" class="compliance-sign__required">*</span>
                  </label>
                  <div class="compliance-sign__checkbox-group">
                    <label
                      v-for="option in field.options"
                      :key="option.id"
                      class="compliance-sign__checkbox-option"
                      :class="{ 'compliance-sign__checkbox-option--exclusive': option.exclusive }"
                    >
                      <input
                        type="checkbox"
                        :checked="isCheckboxChecked(field.id, option.id)"
                        @change="toggleCheckbox(field.id, option.id, option.exclusive, field.options)"
                      />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </div>

                <!-- Text field -->
                <div v-else-if="field.type === 'text'" :key="field.id" class="compliance-sign__field">
                  <label class="compliance-sign__field-label">
                    {{ field.label }}
                    <span v-if="field.required" class="compliance-sign__required">*</span>
                  </label>
                  <el-input :value="formResponses[field.id]" @input="setResponse(field.id, $event)" :placeholder="field.label" />
                </div>
              </template>
            </div>

            <!-- Hidden conditional section notice -->
            <div v-else-if="section.conditionalSection" :key="'hidden-' + sIdx" class="compliance-sign__section compliance-sign__section--hidden">
              <h4 v-if="section.name" class="compliance-sign__section-header compliance-sign__section-header--muted">{{ section.name }}</h4>
              <p class="compliance-sign__section-hidden-text"><em>No further evaluation is needed based on your answers above.</em></p>
            </div>
          </template>
        </div>

        <!-- Signer Info -->
        <div class="compliance-sign__signer">
          <label>{{ signerRoleLabel }}</label>
          <el-input v-model="signerName" placeholder="Enter your full legal name" />
        </div>

        <!-- Signature Pad -->
        <div class="compliance-sign__sig-section">
          <label>Signature</label>
          <div class="compliance-sign__canvas-wrapper">
            <canvas ref="signatureCanvas" class="compliance-sign__canvas"></canvas>
          </div>
          <div class="compliance-sign__sig-actions">
            <el-button size="small" @click="clearSignature">Clear</el-button>
          </div>
        </div>

        <!-- Submit -->
        <div class="text-center mt-4">
          <el-button type="primary" size="medium" @click="submitSignature" :loading="submitting">
            <i class="fa fa-check"></i> Sign & Submit
          </el-button>
        </div>
      </div>
    </div>

    <!-- Success State -->
    <div class="compliance-sign__card" v-if="submitted">
      <div class="text-center py-5">
        <i class="fa fa-check-circle fa-3x" style="color: #67c23a"></i>
        <h3 class="mt-3" style="color: #67c23a">Form Signed Successfully</h3>
        <p class="mt-2" style="color: #e0e0e0">Thank you for signing this form.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";

export default {
  name: "ComplianceSign",
  data() {
    return {
      formData: null,
      loading: true,
      errorMessage: "",
      signerName: "",
      formResponses: {},
      signaturePad: null,
      submitting: false,
      submitted: false,
    };
  },
  async mounted() {
    const token = this.$route.params.token;
    if (!token) {
      this.errorMessage = "Invalid signing link.";
      this.loading = false;
      return;
    }

    try {
      this.formData = await complianceFormsService.getByToken(token);
      if (this.formData.signerRole === "guardian") {
        this.signerName = this.formData.guardianName || this.formData.signerName || "";
      } else {
        this.signerName = this.formData.signerName || "";
      }
      // Restore any previously saved form responses (page refresh persistence)
      if (this.formData.formResponses) {
        this.formResponses = { ...this.formData.formResponses };
      }
      this.$nextTick(() => this.initSignaturePad());
    } catch (error) {
      this.errorMessage = error.response?.data?.message || "Could not load the form.";
    } finally {
      this.loading = false;
    }
  },
  computed: {
    hasFormFields() {
      return this.formData?.formFields && this.formData.formFields.length > 0;
    },
    signerRoleLabel() {
      if (this.formData?.signerRole === "guardian") return "Parent/Guardian Name";
      return "Your Name";
    },
    groupedFields() {
      if (!this.formData?.formFields) return [];
      const sections = [];
      let current = null;
      for (const field of this.formData.formFields) {
        const sectionName = field.section || "";
        if (!current || current.name !== sectionName) {
          current = {
            name: sectionName,
            fields: [],
            description: null,
            conditionalSection: null,
          };
          sections.push(current);
        }
        // Pick up sectionDescription from the first field that has one
        if (field.sectionDescription && !current.description) {
          current.description = field.sectionDescription;
        }
        // Pick up conditionalSection from any field in this section
        if (field.conditionalSection && !current.conditionalSection) {
          current.conditionalSection = field.conditionalSection;
        }
        current.fields.push(field);
      }
      return sections;
    },
    skippedSections() {
      const skipped = [];
      for (const section of this.groupedFields) {
        if (section.conditionalSection && !this.isSectionVisible(section)) {
          skipped.push(section.name);
        }
      }
      return skipped;
    },
  },
  methods: {
    isSectionVisible(section) {
      // If the section has no conditional dependency, always visible
      if (!section.conditionalSection) return true;

      // Find the referenced section and check if any answer in it is "yes"
      const referencedSection = this.groupedFields.find(
        (s) => s.name === section.conditionalSection
      );
      if (!referencedSection) return true;

      return referencedSection.fields.some(
        (f) => this.formResponses[f.id] === "yes"
      );
    },
    formatDescription(text) {
      if (!text) return "";
      // Convert newlines to <br> for display
      return text.replace(/\n/g, "<br>");
    },
    validateFormFields() {
      if (!this.formData?.formFields) return true;
      const required = this.formData.formFields.filter((f) => f.required);
      for (const field of required) {
        // Skip validation for fields in hidden conditional sections
        if (field.conditionalSection && this.skippedSections.includes(field.section)) {
          continue;
        }
        const val = this.formResponses[field.id];
        if (Array.isArray(val) ? val.length === 0 : (!val || String(val).trim() === "")) {
          this.$notifyError(`Please complete: "${field.label}"`);
          return false;
        }
      }
      return true;
    },
    setResponse(fieldId, value) {
      this.$set(this.formResponses, fieldId, value);
    },
    isCheckboxChecked(fieldId, optionId) {
      const val = this.formResponses[fieldId];
      if (!Array.isArray(val)) return false;
      return val.includes(optionId);
    },
    toggleCheckbox(fieldId, optionId, isExclusive, allOptions) {
      let current = Array.isArray(this.formResponses[fieldId])
        ? [...this.formResponses[fieldId]]
        : [];

      if (isExclusive) {
        // Exclusive option: toggle it and clear all others
        current = current.includes(optionId) ? [] : [optionId];
      } else {
        // Regular option: remove any exclusive options first
        const exclusiveIds = (allOptions || []).filter(o => o.exclusive).map(o => o.id);
        current = current.filter(id => !exclusiveIds.includes(id));

        if (current.includes(optionId)) {
          current = current.filter(id => id !== optionId);
        } else {
          current.push(optionId);
        }
      }

      this.$set(this.formResponses, fieldId, current);
    },
    async initSignaturePad() {
      try {
        const SignaturePad = (await import("signature_pad")).default;
        const canvas = this.$refs.signatureCanvas;
        if (canvas) {
          canvas.width = canvas.parentElement.clientWidth;
          canvas.height = 200;
          this.signaturePad = new SignaturePad(canvas, {
            backgroundColor: "rgb(255, 255, 255)",
            penColor: "rgb(0, 0, 0)",
          });
        }
      } catch (err) {
      }
    },
    clearSignature() {
      if (this.signaturePad) this.signaturePad.clear();
    },
    async submitSignature() {
      if (!this.signerName.trim()) {
        this.$notifyError("Please enter your name.");
        return;
      }
      if (!this.validateFormFields()) return;
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        this.$notifyError("Please provide your signature.");
        return;
      }

      this.submitting = true;
      try {
        const signatureImage = this.signaturePad.toDataURL("image/png");
        const token = this.$route.params.token;
        const payload = {
          signature_image: signatureImage,
          signer_name: this.signerName,
        };
        if (this.hasFormFields) {
          payload.form_responses = this.formResponses;
          if (this.skippedSections.length > 0) {
            payload.skipped_sections = this.skippedSections;
          }
        }
        await complianceFormsService.submitSignature(token, payload);
        const portalToken = this.$route.query.portal;
        if (portalToken) {
          const role = this.$route.query.role;
          this.$router.push(`/compliance/${portalToken}${role ? `?role=${role}` : ""}`);
          return;
        }
        this.submitted = true;
      } catch (error) {
        this.$notifyError(error.response?.data?.message || "Error submitting signature.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.compliance-sign {
  display: flex;
  justify-content: center;
  padding: 2rem;
  min-height: 100vh;
  background: rgba(37, 55, 65, 1);
}
.compliance-sign__card {
  background: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
}
.compliance-sign__logo-wrapper {
  text-align: center;
  margin-bottom: 1rem;
}
.compliance-sign__logo {
  height: 60px;
  width: auto;
}
.compliance-sign__title {
  color: #fff;
  text-align: center;
  margin-bottom: 0.5rem;
}
.compliance-sign__client {
  text-align: center;
  color: #b3a6a6;
  margin-bottom: 1.5rem;
}
.compliance-sign__body {
  color: #e0e0e0;
  line-height: 1.7;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin-bottom: 1.5rem;
  max-height: 500px;
  overflow-y: auto;
}
.compliance-sign__body :deep(h2) {
  color: #fff;
  font-size: 1.15rem;
  margin: 0.75rem 0 0.5rem;
}
.compliance-sign__body :deep(h3) {
  color: #fff;
  font-size: 1rem;
  margin: 1rem 0 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.compliance-sign__body :deep(p) {
  margin: 0.5rem 0;
}
.compliance-sign__body :deep(ul),
.compliance-sign__body :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}
.compliance-sign__body :deep(li) {
  margin-bottom: 0.35rem;
}
.compliance-sign__body :deep(ul ul) {
  margin-top: 0.25rem;
}
.compliance-sign__body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
}
.compliance-sign__body :deep(td) {
  padding: 6px 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #e0e0e0;
}
.compliance-sign__body :deep(strong) {
  color: #fff;
}
.compliance-sign__body :deep(em) {
  color: #b3a6a6;
}
/* Integrated form fields layout */
.compliance-sign__integrated {
  margin-bottom: 1.5rem;
}
.compliance-sign__section {
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
}
.compliance-sign__section-desc {
  color: #b3a6a6;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.compliance-sign__section--hidden {
  opacity: 0.5;
  padding: 1rem 1.25rem;
}
.compliance-sign__section-header--muted {
  color: #7f8c8d;
}
.compliance-sign__section-hidden-text {
  color: #7f8c8d;
  font-size: 0.85rem;
  margin: 0;
}
.compliance-sign__signer {
  margin-bottom: 1.5rem;
}
.compliance-sign__signer label {
  display: block;
  color: #e0e0e0;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.compliance-sign__sig-section label {
  display: block;
  color: #e0e0e0;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.compliance-sign__canvas-wrapper {
  border: 2px solid #447DF7;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.compliance-sign__canvas {
  display: block;
  width: 100%;
  cursor: crosshair;
}
.compliance-sign__sig-actions {
  margin-top: 0.5rem;
}
.compliance-sign__section-header {
  color: #447DF7;
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
}
.compliance-sign__field {
  margin-bottom: 1rem;
}
.compliance-sign__field-label {
  display: block;
  color: #e0e0e0;
  font-size: 0.85rem;
  margin-bottom: 0.35rem;
  line-height: 1.5;
}
.compliance-sign__field-label--inline {
  display: inline;
  margin-bottom: 0;
}
.compliance-sign__required {
  color: #FB404B;
  margin-left: 2px;
}
.compliance-sign__yes-no-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}
/* Initials inline row: input on left, label text on right */
.compliance-sign__field--initials {
  margin-bottom: 1.25rem;
}
.compliance-sign__initials-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.compliance-sign__initials-input {
  flex: 0 0 80px;
  max-width: 80px;
}
.compliance-sign__initials-row .compliance-sign__field-label--inline {
  flex: 1;
  min-width: 0;
}
.compliance-sign__checkbox-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
  margin-top: 0.5rem;
}
.compliance-sign__checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.4rem 0;
}
.compliance-sign__checkbox-option input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #447DF7;
  flex-shrink: 0;
}
.compliance-sign__checkbox-option--exclusive {
  grid-column: 1 / -1;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 0.75rem;
  margin-top: 0.25rem;
}
</style>
